import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../utils/api';
import { User } from '../dtos/auth';
import { accessToken, getAuthDetails } from '../utils/helper';
import Cookies from 'universal-cookie';
import { ApiResponseError } from '../dtos/common';

const cookies = new Cookies(null, { path: '/' });

type UserListResponse = { data: User[]; total: number; totalPages: number };
type UserMessageResponse = { message: string };
type UserDetailsResponse = { data: User | null; message?: string };
type ApiKeyResponse = { message: string; apiKey?: string };

const getApiErrorMessage = (error: unknown) => {
  const apiError = error as ApiResponseError;

  return apiError.response?.data?.message || 'Something Went Wrong!';
};

interface UserState {
  userList: UserListResponse;
  selectedUser: UserDetailsResponse;
  loading: boolean;
  error: string | null;
  addUserResponse: UserMessageResponse;
  addUserError: string | null;
  updateUserResponse: UserMessageResponse;
  updateProfileResponse: UserMessageResponse;
  updateUserError: string | null;
  updateProfileError: string | null;
  deleteUserResponse: UserMessageResponse;
  deleteUserError: string | null;
  regenerateApiKeyResponse: ApiKeyResponse;
  regenerateApiKeyError: string | null;
}

const initialState: UserState = {
  userList: { data: [], total: 0, totalPages: 0 },
  selectedUser: { data: null },
  addUserResponse: { message: '' },
  updateUserResponse: { message: '' },
  updateProfileResponse: { message: '' },
  deleteUserResponse: { message: '' },
  regenerateApiKeyResponse: { message: '', apiKey: undefined },
  loading: false,
  error: null,
  addUserError: null,
  updateUserError: null,
  updateProfileError: null,
  deleteUserError: null,
  regenerateApiKeyError: null,
};

export const getAllusersTrigger = createAsyncThunk(
  'get/users',
  async (
    {
      page,
      limit,
      search,
      sortOrder,
      sortField,
    }: {
      page?: number;
      limit?: number;
      search?: string;
      sortOrder?: string;
      sortField?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      // Clean the sortField to remove any malformed characters - use snake_case to match iOS app
      const cleanSortField = sortField ? sortField.split(':')[0] : undefined;

      const queryParams = new URLSearchParams();
      if (page) queryParams.append('page', page.toString());
      if (limit) queryParams.append('limit', limit.toString());
      if (search) queryParams.append('search', search);
      if (sortOrder) queryParams.append('sortOrder', sortOrder);
      if (cleanSortField) queryParams.append('sortField', cleanSortField);

      const response = await api.get(`/users?${queryParams}`, accessToken());
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  },
);

export const addUserTrigger = createAsyncThunk(
  '/users/add',
  async (request: FormData, { rejectWithValue }) => {
    try {
      const response = await api.post<UserMessageResponse>(
        '/users/add',
        request,
        {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${getAuthDetails().accessToken}`,
            'Content-Type': 'multipart/form-data',
            'x-csrf-token': cookies.get('XSRF-TOKEN'),
          },
        },
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  },
);

export const updateUserTrigger = createAsyncThunk(
  '/users/update/:id',
  async ({ id, request }: { id: number; request: FormData }, { rejectWithValue }) => {
    try {
      const response = await api.patch<UserMessageResponse>(
        `/users/update/${id}`,
        request,
        {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${getAuthDetails().accessToken}`,
            'Content-Type': 'multipart/form-data',
            'x-csrf-token': cookies.get('XSRF-TOKEN'),
          },
        },
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  },
);

export const updateProfileTrigger = createAsyncThunk(
  '/about/update/:id',
  async ({ id, request }: { id: number; request: FormData }, { rejectWithValue }) => {
    try {
      const response = await api.patch<UserMessageResponse>(
        `/about/update/${id}`,
        request,
        {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${getAuthDetails().accessToken}`,
            'Content-Type': 'multipart/form-data',
            'x-csrf-token': cookies.get('XSRF-TOKEN'),
          },
        },
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  },
);

export const deleteUserTrigger = createAsyncThunk(
  '/users/delete/:id',
  async (id: number | undefined, { rejectWithValue }) => {
    try {
      const response = await api.delete<UserMessageResponse>(
        `/users/delete/${id}`,
        accessToken(),
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  },
);

export const regenerateApiKeyTrigger = createAsyncThunk(
  'regenerate/api-key',
  async (userId: number, { rejectWithValue }) => {
    try {
      const response = await api.patch<ApiKeyResponse>(
        `/users/${userId}/regenerate-api-key`,
        {},
        accessToken(),
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  },
);

export const getUserByIdTrigger = createAsyncThunk(
  '/users/getById',
  async (id: number | undefined, { rejectWithValue }) => {
    try {
      const response = await api.get<UserDetailsResponse>(`/about/${id}`, accessToken());
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  },
);

// Create slice
const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAllusersTrigger.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllusersTrigger.fulfilled, (state, action) => {
        state.loading = false;
        state.userList = action.payload;
      })
      .addCase(getAllusersTrigger.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || action.error.message || 'Failed to load users';
      })
      .addCase(addUserTrigger.pending, (state) => {
        state.loading = true;
      })
      .addCase(addUserTrigger.fulfilled, (state, action) => {
        state.loading = false;
        state.addUserResponse = action.payload;
      })
      .addCase(addUserTrigger.rejected, (state, action) => {
        state.loading = false;
        state.addUserError =
          (action.payload as string) || action.error.message || 'Failed to add user';
      })
      .addCase(updateUserTrigger.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateUserTrigger.fulfilled, (state, action) => {
        state.loading = false;
        state.updateUserResponse = action.payload;
      })
      .addCase(updateUserTrigger.rejected, (state, action) => {
        state.loading = false;
        state.updateUserError =
          (action.payload as string) || action.error.message || 'Failed to update user';
      })
      .addCase(updateProfileTrigger.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateProfileTrigger.fulfilled, (state, action) => {
        state.loading = false;
        state.updateProfileResponse = action.payload;
      })
      .addCase(updateProfileTrigger.rejected, (state, action) => {
        state.loading = false;
        state.updateProfileError =
          (action.payload as string) || action.error.message || 'Failed to update profile';
      })
      .addCase(deleteUserTrigger.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteUserTrigger.fulfilled, (state, action) => {
        state.loading = false;
        state.deleteUserResponse = action.payload;
      })
      .addCase(deleteUserTrigger.rejected, (state, action) => {
        state.loading = false;
        state.deleteUserError =
          (action.payload as string) || action.error.message || 'Failed to delete user';
      })
      .addCase(regenerateApiKeyTrigger.pending, (state) => {
        state.loading = true;
      })
      .addCase(regenerateApiKeyTrigger.fulfilled, (state, action) => {
        state.loading = false;
        state.regenerateApiKeyResponse = action.payload;
      })
      .addCase(regenerateApiKeyTrigger.rejected, (state, action) => {
        state.loading = false;
        state.regenerateApiKeyError =
          (action.payload as string) || action.error.message || 'Failed to regenerate API key';
      })
      .addCase(getUserByIdTrigger.pending, (state) => {
        state.loading = true;
      })
      .addCase(getUserByIdTrigger.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedUser = action.payload;
      })
      .addCase(getUserByIdTrigger.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || action.error.message || 'Failed to load user';
      });
  },
});

// Export the reducer to be added to the store
export default userSlice.reducer;
